import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

const ENQUIRY_TYPES = [
  { value: "strategy", label: "Strategy" },
  { value: "regulatory", label: "Regulatory" },
  { value: "digital", label: "Digital" },
  { value: "transactions", label: "Transactions" },
  { value: "general", label: "General enquiry" },
];

const initial = {
  name: "",
  email: "",
  company: "",
  enquiryType: "",
  message: "",
};

const ContactModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    // Concept site — simulate send
    await new Promise((r) => setTimeout(r, 700));
    setSubmitting(false);
    toast.success("Thank you. We'll be in touch shortly.", {
      description: "Your enquiry has been received.",
    });
    setForm(initial);
    onOpenChange(false);
  };

  const fieldClass =
    "bg-transparent border border-[#1f1d18] focus-visible:border-gold focus-visible:ring-0 focus-visible:ring-offset-0 text-offwhite placeholder:text-[#5e594f] rounded-none h-11";

  const labelClass =
    "block mb-2 text-[12px] tracking-[0.14em] uppercase text-offwhite/70";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-modal"
        className="bg-[#0E0E0E] border border-hairline rounded-none p-0 max-w-[520px] w-[92vw] max-h-[92vh] overflow-y-auto sm:rounded-none"
      >
        <div className="px-8 pt-8 pb-2">
          <DialogHeader>
            <DialogTitle className="font-serif text-[28px] text-offwhite font-normal tracking-tight">
              Start a conversation
            </DialogTitle>
            <DialogDescription className="text-offwhite/60 text-[13px] mt-1">
              Tell us about your goals and challenges. We'll be in touch to
              explore how we can help.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="h-px bg-gold/25 mx-8 mt-4" />

        <form
          onSubmit={onSubmit}
          className="px-8 pb-8 pt-6 space-y-5"
          data-testid="contact-form"
        >
          <div>
            <label className={labelClass} htmlFor="cm-name">
              Name
            </label>
            <Input
              id="cm-name"
              data-testid="contact-input-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className={fieldClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="cm-email">
              Email
            </label>
            <Input
              id="cm-email"
              type="email"
              data-testid="contact-input-email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@company.com"
              className={fieldClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="cm-company">
              Company
            </label>
            <Input
              id="cm-company"
              data-testid="contact-input-company"
              value={form.company}
              onChange={update("company")}
              placeholder="Your company"
              className={fieldClass}
            />
          </div>

          <div>
            <label className={labelClass}>Type of enquiry</label>
            <Select
              value={form.enquiryType}
              onValueChange={(v) => setForm((f) => ({ ...f, enquiryType: v }))}
            >
              <SelectTrigger
                data-testid="contact-select-enquiry"
                className={`${fieldClass} text-left flex justify-between`}
              >
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent
                className="bg-[#111111] border border-hairline rounded-none"
                data-testid="contact-select-content"
              >
                {ENQUIRY_TYPES.map((t) => (
                  <SelectItem
                    key={t.value}
                    value={t.value}
                    className="text-offwhite/90 focus:bg-gold/10 focus:text-gold rounded-none"
                  >
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className={labelClass} htmlFor="cm-message">
              Message
            </label>
            <Textarea
              id="cm-message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about your enquiry"
              rows={4}
              className={`${fieldClass} h-auto py-3 resize-none`}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="w-full mt-2 py-3.5 bg-gold text-[#0A0A0A] text-[13px] tracking-[0.06em] font-medium hover:bg-[#D9BC85] transition-colors disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Send enquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
